import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Palette, Globe, Copy, Check, Settings, ExternalLink, 
  Phone, Mail, MapPin, Clock, Users, Calendar, Plus, Trash2,
  GripVertical, Tag, Layers, FileText, Gift, Upload, Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';
import InsuranceFormsSettings from './settings/InsuranceFormsSettings';
import BrandPreview from './settings/BrandPreview';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PortalSettingsComponent = () => {
  const [settings, setSettings] = useState({
    studio_name: '',
    studio_subtitle: 'Kundenportal',
    primary_color: '#22c55e',
    secondary_color: '#10b981',
    accent_color: '#059669',
    brand_accent_color: '',
    background_gradient_from: '#f0fdf4',
    background_gradient_to: '#dcfce7',
    logo_url: '',
    contact_phone: '',
    contact_email: '',
    address: '',
    booking_notice: '',
    min_booking_hours: 2,
    max_booking_days: 30,
    cancellation_hours: 24,
    show_trainer_selection: true,
    show_resource_selection: false,
    require_login_to_book: true,
    enable_registration: true,
  });
  const [embedUrl, setEmbedUrl] = useState('');
  const [embedCode, setEmbedCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Service Categories State
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Add-on Services State
  const [addonSettings, setAddonSettings] = useState({
    enabled: false,
    assistant_trainer_id: null,
    warmup_times: {},
    mappings: []
  });
  const [trainers, setTrainers] = useState([]);
  const [savingAddons, setSavingAddons] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [applyingAccent, setApplyingAccent] = useState(false);

  useEffect(() => {
    loadSettings();
    loadServices();
    loadTrainers();
    loadAddonSettings();
  }, []);

  const loadTrainers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users?role=trainer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Also get admins who can act as trainers
      const adminsResponse = await axios.get(`${API}/users?role=admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrainers([...(response.data || []), ...(adminsResponse.data || [])]);
    } catch (error) {
      console.error('Load trainers error:', error);
    }
  };

  const loadAddonSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/addon-services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddonSettings(response.data);
    } catch (error) {
      console.error('Load addon settings error:', error);
    }
  };

  const saveAddonSettings = async () => {
    setSavingAddons(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings/addon-services`, addonSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Zusatzleistungen gespeichert');
    } catch (error) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSavingAddons(false);
    }
  };

  const addAddonMapping = () => {
    setAddonSettings(prev => ({
      ...prev,
      mappings: [
        ...prev.mappings,
        {
          id: Date.now().toString(),
          main_service_id: '',
          addon_service_id: '',
          combo_price: 0,
          is_active: true
        }
      ]
    }));
  };

  const updateAddonMapping = (index, field, value) => {
    setAddonSettings(prev => {
      const newMappings = [...prev.mappings];
      newMappings[index] = { ...newMappings[index], [field]: value };
      return { ...prev, mappings: newMappings };
    });
  };

  const removeAddonMapping = (index) => {
    setAddonSettings(prev => ({
      ...prev,
      mappings: prev.mappings.filter((_, i) => i !== index)
    }));
  };

  const loadServices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServices(response.data || []);
    } catch (error) {
      console.error('Load services error:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/portal/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, ...response.data }));
      setEmbedUrl(response.data.embed_url || '');
      setEmbedCode(response.data.embed_code || '');
      setCategories(response.data.service_categories || []);
    } catch (error) {
      console.error('Load settings error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/portal/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Einstellungen gespeichert');
      loadSettings(); // Reload to get updated embed code
    } catch (error) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/portal/settings/logo`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const url = res.data?.logo_url || '';
      setSettings(prev => ({ ...prev, logo_url: url }));
      const suggested = res.data?.suggested_color;
      if (suggested) {
        toast.success('Logo hochgeladen', {
          description: `Vorgeschlagene Marken-Farbe: ${suggested}`,
          action: {
            label: 'Übernehmen',
            onClick: () => applyBrandAccent(suggested),
          },
        });
      } else {
        toast.success('Logo hochgeladen');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Logo-Upload fehlgeschlagen');
    } finally {
      setUploadingLogo(false);
    }
  };

  const applyBrandAccent = async (colorArg) => {
    const color = colorArg || settings.brand_accent_color || settings.primary_color;
    setApplyingAccent(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/portal/settings/brand-accent`, { color }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const applied = res.data?.color || color;
      setSettings(prev => ({
        ...prev,
        brand_accent_color: applied,
        primary_color: applied,
        button_color: applied,
        accent_color: applied,
      }));
      toast.success('Marken-Farbe auf alle Kanäle angewendet');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anwenden fehlgeschlagen');
    } finally {
      setApplyingAccent(false);
    }
  };

  const copyToClipboard = (text) => {    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('In Zwischenablage kopiert');
    setTimeout(() => setCopied(false), 2000);
  };

  // Category Management Functions
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/portal/settings/categories`,
        { name: newCategoryName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCategories([...categories, response.data]);
      setNewCategoryName('');
      toast.success('Kategorie erstellt');
    } catch (error) {
      toast.error('Kategorie erstellen fehlgeschlagen');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Kategorie wirklich löschen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/portal/settings/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(categories.filter(c => c.id !== categoryId));
      toast.success('Kategorie gelöscht');
    } catch (error) {
      toast.error('Kategorie löschen fehlgeschlagen');
    }
  };

  const handleUpdateCategory = async (categoryId, updates) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/portal/settings/categories/${categoryId}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCategories(categories.map(c => 
        c.id === categoryId ? { ...c, ...updates } : c
      ));
      toast.success('Kategorie aktualisiert');
    } catch (error) {
      toast.error('Kategorie aktualisieren fehlgeschlagen');
    }
  };

  const handleToggleService = async (categoryId, serviceId) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return;
    
    const currentServices = category.service_ids || [];
    const newServices = currentServices.includes(serviceId)
      ? currentServices.filter(id => id !== serviceId)
      : [...currentServices, serviceId];
    
    await handleUpdateCategory(categoryId, { service_ids: newServices });
  };

  const getServiceById = (serviceId) => {
    return services.find(s => s.id === serviceId);
  };

  const getAssignedCategoryId = (serviceId) => {
    for (const cat of categories) {
      if ((cat.service_ids || []).includes(serviceId)) {
        return cat.id;
      }
    }
    return null;
  };

  const colorPresets = [
    { name: 'Grün (Standard)', primary: '#22c55e', secondary: '#10b981', bgFrom: '#f0fdf4', bgTo: '#dcfce7' },
    { name: 'Blau', primary: '#3b82f6', secondary: '#2563eb', bgFrom: '#eff6ff', bgTo: '#dbeafe' },
    { name: 'Lila', primary: '#8b5cf6', secondary: '#7c3aed', bgFrom: '#f5f3ff', bgTo: '#ede9fe' },
    { name: 'Orange', primary: '#f97316', secondary: '#ea580c', bgFrom: '#fff7ed', bgTo: '#ffedd5' },
    { name: 'Rot', primary: '#ef4444', secondary: '#dc2626', bgFrom: '#fef2f2', bgTo: '#fee2e2' },
    { name: 'Türkis', primary: '#14b8a6', secondary: '#0d9488', bgFrom: '#f0fdfa', bgTo: '#ccfbf1' },
  ];

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 flex items-center justify-center">
          <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-green-500" />
            Kundenportal Einstellungen
          </CardTitle>
          <CardDescription>
            Passen Sie das Erscheinungsbild und die Funktionen Ihres Kundenportals an
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="appearance" className="space-y-6">
            <TabsList className="grid grid-cols-7 w-full max-w-5xl">
              <TabsTrigger value="appearance" className="gap-2">
                <Palette size={16} />
                Aussehen
              </TabsTrigger>
              <TabsTrigger value="categories" className="gap-2">
                <Layers size={16} />
                Kategorien
              </TabsTrigger>
              <TabsTrigger value="content" className="gap-2">
                <Settings size={16} />
                Inhalt
              </TabsTrigger>
              <TabsTrigger value="booking" className="gap-2">
                <Calendar size={16} />
                Buchung
              </TabsTrigger>
              <TabsTrigger value="addons" className="gap-2">
                <Gift size={16} />
                Zusatz
              </TabsTrigger>
              <TabsTrigger value="insurance" className="gap-2">
                <FileText size={16} />
                Formulare
              </TabsTrigger>
              <TabsTrigger value="embed" className="gap-2">
                <Globe size={16} />
                Einbettung
              </TabsTrigger>
            </TabsList>

            {/* Appearance Tab */}
            <TabsContent value="appearance" className="space-y-6">
              {/* Marken-Logo & Haupt-Akzentfarbe */}
              <div className="rounded-xl border border-gray-200 p-5 space-y-5" data-testid="brand-identity-section">
                <div className="flex items-center gap-2">
                  <Palette size={18} className="text-gray-700" />
                  <h3 className="font-semibold text-gray-900">Marke (Logo & Farbe)</h3>
                </div>

                {/* Logo Upload */}
                <div className="grid md:grid-cols-[auto,1fr] gap-4 items-start">
                  <div
                    className="w-24 h-24 rounded-xl border border-dashed border-gray-300 flex items-center justify-center bg-gray-50 overflow-hidden shrink-0"
                    data-testid="brand-logo-preview"
                  >
                    {settings.logo_url ? (
                      <img src={settings.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                    ) : (
                      <ImageIcon className="h-8 w-8 text-gray-300" />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Betriebs-Logo</Label>
                    <p className="text-xs text-gray-500">Erscheint in E-Mails, Portal und App. PNG/JPG/WEBP/SVG, max. 5 MB.</p>
                    <div className="flex items-center gap-2">
                      <input
                        id="brand-logo-input"
                        type="file"
                        accept=".png,.jpg,.jpeg,.webp,.svg,image/*"
                        className="hidden"
                        onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                        data-testid="brand-logo-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={uploadingLogo}
                        onClick={() => document.getElementById('brand-logo-input')?.click()}
                        data-testid="brand-logo-upload-btn"
                      >
                        <Upload size={14} className="mr-1.5" />
                        {uploadingLogo ? 'Lädt…' : 'Logo hochladen'}
                      </Button>
                      {settings.logo_url && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setSettings({ ...settings, logo_url: '' })}
                          data-testid="brand-logo-remove-btn"
                        >
                          <Trash2 size={14} className="mr-1.5" /> Entfernen
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Haupt-Akzentfarbe */}
                <div className="border-t border-gray-100 pt-4">
                  <Label htmlFor="brand_accent_color" className="text-sm font-medium">Haupt-Akzentfarbe</Label>
                  <p className="text-xs text-gray-500 mb-2">Eine Farbe für die ganze Marke. Mit „Auf alle Kanäle anwenden" wird sie in Portal, E-Mail und App übernommen.</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      type="color"
                      id="brand_accent_color"
                      value={settings.brand_accent_color || settings.primary_color || '#22c55e'}
                      onChange={(e) => setSettings({ ...settings, brand_accent_color: e.target.value })}
                      className="w-12 h-10 p-1 cursor-pointer"
                      data-testid="brand-accent-color-picker"
                    />
                    <Input
                      type="text"
                      value={settings.brand_accent_color || ''}
                      placeholder={settings.primary_color}
                      onChange={(e) => setSettings({ ...settings, brand_accent_color: e.target.value })}
                      className="w-36"
                      data-testid="brand-accent-color-text"
                    />
                    <Button
                      type="button"
                      size="sm"
                      disabled={applyingAccent}
                      onClick={() => applyBrandAccent()}
                      style={{ backgroundColor: settings.brand_accent_color || settings.primary_color }}
                      className="text-white"
                      data-testid="brand-accent-apply-btn"
                    >
                      {applyingAccent ? 'Wird angewendet…' : 'Auf alle Kanäle anwenden'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Color Presets */}
              <div>
                <Label className="text-sm font-medium mb-3 block">Farbschema Vorlagen</Label>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {colorPresets.map(preset => (
                    <button
                      key={preset.name}
                      onClick={() => setSettings({
                        ...settings,
                        primary_color: preset.primary,
                        secondary_color: preset.secondary,
                        background_gradient_from: preset.bgFrom,
                        background_gradient_to: preset.bgTo
                      })}
                      className="p-3 rounded-lg border-2 hover:border-gray-400 transition-all text-center"
                      style={{ 
                        borderColor: settings.primary_color === preset.primary ? preset.primary : '#e5e7eb',
                        background: `linear-gradient(135deg, ${preset.bgFrom} 0%, ${preset.bgTo} 100%)`
                      }}
                    >
                      <div 
                        className="w-8 h-8 rounded-full mx-auto mb-2"
                        style={{ backgroundColor: preset.primary }}
                      />
                      <span className="text-xs font-medium text-gray-700">{preset.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Colors */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="primary_color">Primärfarbe</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      id="primary_color"
                      value={settings.primary_color}
                      onChange={(e) => setSettings({...settings, primary_color: e.target.value})}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={settings.primary_color}
                      onChange={(e) => setSettings({...settings, primary_color: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="secondary_color">Sekundärfarbe</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      id="secondary_color"
                      value={settings.secondary_color}
                      onChange={(e) => setSettings({...settings, secondary_color: e.target.value})}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={settings.secondary_color}
                      onChange={(e) => setSettings({...settings, secondary_color: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="bg_from">Hintergrund Von</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      id="bg_from"
                      value={settings.background_gradient_from}
                      onChange={(e) => setSettings({...settings, background_gradient_from: e.target.value})}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={settings.background_gradient_from}
                      onChange={(e) => setSettings({...settings, background_gradient_from: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="bg_to">Hintergrund Bis</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      id="bg_to"
                      value={settings.background_gradient_to}
                      onChange={(e) => setSettings({...settings, background_gradient_to: e.target.value})}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={settings.background_gradient_to}
                      onChange={(e) => setSettings({...settings, background_gradient_to: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              {/* Marken-Vorschau: E-Mail, Portal, App */}
              <BrandPreview settings={settings} />
            </TabsContent>

            {/* Categories Tab */}
            <TabsContent value="categories" className="space-y-6">
              <div>
                <Label className="text-sm font-medium mb-3 block">Service-Kategorien</Label>
                <p className="text-sm text-gray-500 mb-4">
                  Gruppieren Sie Ihre Services in Kategorien (z.B. &quot;Personal Training&quot;, &quot;EMS Training&quot;).
                  Die Kategorien werden im Kundenportal in der definierten Reihenfolge angezeigt.
                </p>
                
                {/* Create New Category */}
                <div className="flex gap-2 mb-6">
                  <Input
                    placeholder="Neue Kategorie (z.B. Personal Training)"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateCategory()}
                    className="flex-1"
                  />
                  <Button onClick={handleCreateCategory} disabled={!newCategoryName.trim()}>
                    <Plus size={16} className="mr-1" />
                    Kategorie erstellen
                  </Button>
                </div>

                {/* Category List */}
                {categories.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
                    <Layers className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-500">Keine Kategorien vorhanden</p>
                    <p className="text-sm text-gray-400">Erstellen Sie Ihre erste Kategorie oben</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {categories.map((category, index) => (
                      <div key={category.id} className="border rounded-lg overflow-hidden">
                        {/* Category Header */}
                        <div className="bg-gray-50 p-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <GripVertical className="h-5 w-5 text-gray-400 cursor-grab" />
                            {editingCategory === category.id ? (
                              <Input
                                value={category.name}
                                onChange={(e) => setCategories(categories.map(c => 
                                  c.id === category.id ? { ...c, name: e.target.value } : c
                                ))}
                                onBlur={() => {
                                  handleUpdateCategory(category.id, { name: category.name });
                                  setEditingCategory(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleUpdateCategory(category.id, { name: category.name });
                                    setEditingCategory(null);
                                  }
                                }}
                                className="h-8 w-48"
                                autoFocus
                              />
                            ) : (
                              <span 
                                className="font-medium cursor-pointer hover:text-blue-600"
                                onClick={() => setEditingCategory(category.id)}
                              >
                                {category.name}
                              </span>
                            )}
                            <Badge variant="outline" className="text-xs">
                              {(category.service_ids || []).length} Services
                            </Badge>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteCategory(category.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                        
                        {/* Service Assignment */}
                        <div className="p-3">
                          <p className="text-xs text-gray-500 mb-2">Services in dieser Kategorie:</p>
                          <div className="flex flex-wrap gap-2">
                            {services.filter(s => s.is_active !== false).map(service => {
                              const isAssigned = (category.service_ids || []).includes(service.id);
                              const assignedToOther = !isAssigned && getAssignedCategoryId(service.id);
                              
                              return (
                                <button
                                  key={service.id}
                                  onClick={() => {
                                    if (assignedToOther) {
                                      // Remove from other category first
                                      handleUpdateCategory(assignedToOther, {
                                        service_ids: categories.find(c => c.id === assignedToOther)?.service_ids?.filter(id => id !== service.id) || []
                                      });
                                    }
                                    handleToggleService(category.id, service.id);
                                  }}
                                  className={`
                                    px-3 py-1.5 rounded-full text-sm transition-all
                                    ${isAssigned 
                                      ? 'bg-green-100 text-green-800 border-2 border-green-500' 
                                      : assignedToOther
                                        ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-pointer'
                                        : 'bg-gray-100 text-gray-700 border border-gray-200 hover:border-green-400'
                                    }
                                  `}
                                  title={assignedToOther ? `Aktuell in: ${categories.find(c => c.id === assignedToOther)?.name}` : ''}
                                >
                                  {isAssigned && <Check size={12} className="inline mr-1" />}
                                  {service.name}
                                  <span className="text-xs opacity-70 ml-1">
                                    ({service.duration_minutes || service.duration || 60} Min)
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Uncategorized Services Info */}
                {services.filter(s => s.is_active !== false && !getAssignedCategoryId(s.id)).length > 0 && (
                  <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-sm text-yellow-800">
                      <Tag size={14} className="inline mr-1" />
                      <strong>{services.filter(s => s.is_active !== false && !getAssignedCategoryId(s.id)).length} Services</strong> ohne Kategorie werden im Portal unter &quot;Weitere Services&quot; angezeigt.
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Content Tab */}
            <TabsContent value="content" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="studio_name">Studio Name</Label>
                  <Input
                    id="studio_name"
                    value={settings.studio_name}
                    onChange={(e) => setSettings({...settings, studio_name: e.target.value})}
                    placeholder="z.B. Mein Studio"
                  />
                </div>
                <div>
                  <Label htmlFor="studio_subtitle">Untertitel</Label>
                  <Input
                    id="studio_subtitle"
                    value={settings.studio_subtitle}
                    onChange={(e) => setSettings({...settings, studio_subtitle: e.target.value})}
                    placeholder="Kundenportal"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="logo_url">Logo URL (optional)</Label>
                <Input
                  id="logo_url"
                  value={settings.logo_url || ''}
                  onChange={(e) => setSettings({...settings, logo_url: e.target.value})}
                  placeholder="https://example.com/logo.png"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_phone" className="flex items-center gap-2">
                    <Phone size={14} />
                    Telefon
                  </Label>
                  <Input
                    id="contact_phone"
                    value={settings.contact_phone || ''}
                    onChange={(e) => setSettings({...settings, contact_phone: e.target.value})}
                    placeholder="+41 79 123 45 67"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_email" className="flex items-center gap-2">
                    <Mail size={14} />
                    E-Mail
                  </Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={settings.contact_email || ''}
                    onChange={(e) => setSettings({...settings, contact_email: e.target.value})}
                    placeholder="info@strongfit.ch"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="address" className="flex items-center gap-2">
                  <MapPin size={14} />
                  Adresse
                </Label>
                <Input
                  id="address"
                  value={settings.address || ''}
                  onChange={(e) => setSettings({...settings, address: e.target.value})}
                  placeholder="Weite Gasse 14, 5400 Baden"
                />
              </div>

              <div>
                <Label htmlFor="booking_notice">Buchungshinweis (wird bei Terminauswahl angezeigt)</Label>
                <Input
                  id="booking_notice"
                  value={settings.booking_notice || ''}
                  onChange={(e) => setSettings({...settings, booking_notice: e.target.value})}
                  placeholder="Bitte beachten Sie unsere Stornierungsbedingungen."
                />
              </div>
            </TabsContent>

            {/* Booking Tab */}
            <TabsContent value="booking" className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="min_booking_hours" className="flex items-center gap-2">
                    <Clock size={14} />
                    Min. Vorlaufzeit (Stunden)
                  </Label>
                  <Input
                    id="min_booking_hours"
                    type="number"
                    min={0}
                    value={settings.min_booking_hours}
                    onChange={(e) => setSettings({...settings, min_booking_hours: parseInt(e.target.value) || 0})}
                  />
                  <p className="text-xs text-gray-500 mt-1">Wie viele Stunden vor dem Termin muss gebucht werden</p>
                </div>
                <div>
                  <Label htmlFor="max_booking_days">Max. Buchung im Voraus (Tage)</Label>
                  <Input
                    id="max_booking_days"
                    type="number"
                    min={1}
                    value={settings.max_booking_days}
                    onChange={(e) => setSettings({...settings, max_booking_days: parseInt(e.target.value) || 30})}
                  />
                  <p className="text-xs text-gray-500 mt-1">Wie viele Tage im Voraus kann gebucht werden</p>
                </div>
                <div>
                  <Label htmlFor="cancellation_hours">Stornierungsfrist (Stunden)</Label>
                  <Input
                    id="cancellation_hours"
                    type="number"
                    min={0}
                    value={settings.cancellation_hours}
                    onChange={(e) => setSettings({...settings, cancellation_hours: parseInt(e.target.value) || 24})}
                  />
                  <p className="text-xs text-gray-500 mt-1">Bis wie viele Stunden vor dem Termin kann storniert werden</p>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Users size={14} />
                      Trainer-Auswahl anzeigen
                    </Label>
                    <p className="text-xs text-gray-500">Kunden können einen Trainer auswählen</p>
                  </div>
                  <Switch
                    checked={settings.show_trainer_selection}
                    onCheckedChange={(checked) => setSettings({...settings, show_trainer_selection: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Raum-Auswahl anzeigen</Label>
                    <p className="text-xs text-gray-500">Kunden können einen Raum/Ressource auswählen</p>
                  </div>
                  <Switch
                    checked={settings.show_resource_selection}
                    onCheckedChange={(checked) => setSettings({...settings, show_resource_selection: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Login erforderlich zum Buchen</Label>
                    <p className="text-xs text-gray-500">Kunden müssen angemeldet sein, um zu buchen</p>
                  </div>
                  <Switch
                    checked={settings.require_login_to_book}
                    onCheckedChange={(checked) => setSettings({...settings, require_login_to_book: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Registrierung erlauben</Label>
                    <p className="text-xs text-gray-500">Neue Kunden können sich selbst registrieren</p>
                  </div>
                  <Switch
                    checked={settings.enable_registration}
                    onCheckedChange={(checked) => setSettings({...settings, enable_registration: checked})}
                  />
                </div>
              </div>
            </TabsContent>

            {/* Add-ons Tab */}
            <TabsContent value="addons" className="space-y-6">
              <div className="space-y-6">
                {/* Enable Add-ons */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Zusatzleistungen aktivieren</Label>
                    <p className="text-sm text-gray-500">Kunden können nach einer Buchung Zusatzleistungen hinzufügen</p>
                  </div>
                  <Switch
                    checked={addonSettings.enabled}
                    onCheckedChange={(checked) => setAddonSettings(prev => ({ ...prev, enabled: checked }))}
                  />
                </div>

                {addonSettings.enabled && (
                  <>
                    {/* Assistant Trainer Selection */}
                    <div className="space-y-2">
                      <Label>Assistent (Ersatz-Trainer für Zusatzleistungen)</Label>
                      <p className="text-xs text-gray-500 mb-2">
                        Dieser Trainer wird für Zusatzleistungen verwendet, wenn der Haupttrainer nicht verfügbar ist.
                        Wählen Sie einen Trainer, der für Kunden nicht sichtbar sein soll.
                      </p>
                      <select
                        className="w-full p-2 border rounded-md"
                        value={addonSettings.assistant_trainer_id || ''}
                        onChange={(e) => setAddonSettings(prev => ({ ...prev, assistant_trainer_id: e.target.value || null }))}
                      >
                        <option value="">-- Assistent auswählen --</option>
                        {trainers.map(trainer => (
                          <option key={trainer.id} value={trainer.id}>
                            {trainer.first_name} {trainer.last_name} ({trainer.role})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Warmup Times */}
                    <div className="space-y-2">
                      <Label>Aufwärmzeiten pro Service (intern, nicht sichtbar für Kunden)</Label>
                      <p className="text-xs text-gray-500 mb-2">
                        Diese Zeit wird intern genutzt, um Zusatzleistungen während der Aufwärmphase einzuplanen.
                        Die Slot-Dauer des Services bleibt unverändert.
                      </p>
                      <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-2">
                        {services.filter(s => s.is_active).map(service => (
                          <div key={service.id} className="flex items-center gap-2">
                            <span className="flex-1 text-sm">{service.name}</span>
                            <Input
                              type="number"
                              min="0"
                              max="30"
                              className="w-20"
                              placeholder="0"
                              value={addonSettings.warmup_times?.[service.id] || ''}
                              onChange={(e) => setAddonSettings(prev => ({
                                ...prev,
                                warmup_times: {
                                  ...prev.warmup_times,
                                  [service.id]: parseInt(e.target.value) || 0
                                }
                              }))}
                            />
                            <span className="text-xs text-gray-500">min</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Add-on Mappings */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-base">Add-on Zuordnungen</Label>
                          <p className="text-xs text-gray-500">Definieren Sie, welche Zusatzleistungen zu welchen Services angeboten werden</p>
                        </div>
                        <Button onClick={addAddonMapping} size="sm" variant="outline">
                          <Plus size={16} className="mr-1" /> Hinzufügen
                        </Button>
                      </div>

                      {addonSettings.mappings?.length === 0 && (
                        <p className="text-center text-gray-500 py-4 border rounded-md">
                          Keine Zuordnungen konfiguriert. Klicken Sie auf "Hinzufügen" um zu beginnen.
                        </p>
                      )}

                      {addonSettings.mappings?.map((mapping, index) => (
                        <div key={mapping.id} className="p-4 border rounded-lg space-y-3 bg-gray-50">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm">Zuordnung #{index + 1}</span>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={mapping.is_active}
                                onCheckedChange={(checked) => updateAddonMapping(index, 'is_active', checked)}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeAddonMapping(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <Label className="text-xs">Hauptservice</Label>
                              <select
                                className="w-full p-2 border rounded-md text-sm"
                                value={mapping.main_service_id}
                                onChange={(e) => updateAddonMapping(index, 'main_service_id', e.target.value)}
                              >
                                <option value="">-- Auswählen --</option>
                                {services.filter(s => s.is_active).map(service => (
                                  <option key={service.id} value={service.id}>
                                    {service.name} ({service.duration}min, CHF {service.price})
                                  </option>
                                ))}
                              </select>
                            </div>
                            
                            <div>
                              <Label className="text-xs">Zusatzleistung</Label>
                              <select
                                className="w-full p-2 border rounded-md text-sm"
                                value={mapping.addon_service_id}
                                onChange={(e) => updateAddonMapping(index, 'addon_service_id', e.target.value)}
                              >
                                <option value="">-- Auswählen --</option>
                                {services.filter(s => s.is_active).map(service => (
                                  <option key={service.id} value={service.id}>
                                    {service.name} ({service.duration}min, CHF {service.price})
                                  </option>
                                ))}
                              </select>
                            </div>
                            
                            <div>
                              <Label className="text-xs">Kombi-Preis (CHF)</Label>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="35.00"
                                value={mapping.combo_price}
                                onChange={(e) => updateAddonMapping(index, 'combo_price', parseFloat(e.target.value) || 0)}
                              />
                            </div>
                          </div>
                          
                          {mapping.main_service_id && mapping.addon_service_id && (
                            <div className="text-xs text-green-600 bg-green-50 p-2 rounded">
                              ✓ {services.find(s => s.id === mapping.main_service_id)?.name} → {services.find(s => s.id === mapping.addon_service_id)?.name} für CHF {mapping.combo_price} (statt CHF {services.find(s => s.id === mapping.addon_service_id)?.price})
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <Button onClick={saveAddonSettings} disabled={savingAddons} className="w-full">
                      {savingAddons ? 'Speichern...' : 'Zusatzleistungen speichern'}
                    </Button>
                  </>
                )}
              </div>
            </TabsContent>

            {/* Insurance Forms Tab */}
            <TabsContent value="insurance" className="space-y-6">
              <InsuranceFormsSettings />
            </TabsContent>

            {/* Embed Tab */}
            <TabsContent value="embed" className="space-y-6">
              {/* Portal URL */}
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Globe size={14} />
                  Portal URL
                </Label>
                <div className="flex gap-2">
                  <Input value={embedUrl} readOnly className="bg-gray-50 font-mono text-sm" />
                  <Button variant="outline" onClick={() => copyToClipboard(embedUrl)}>
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </Button>
                  <Button variant="outline" asChild>
                    <a href={embedUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink size={16} />
                    </a>
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Diese URL können Sie direkt verlinken oder in Ihre Website einbetten
                </p>
              </div>

              {/* Embed Code */}
              <div>
                <Label className="mb-2 block">Einbettungs-Code für Ihre Website</Label>
                <div className="relative">
                  <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm font-mono">
                    {embedCode}
                  </pre>
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard(embedCode)}
                  >
                    {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
                    Kopieren
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Kopieren Sie diesen Code und fügen Sie ihn in Ihre Website (z.B. www.strongfit.ch) ein.
                </p>
              </div>

              {/* Live Preview */}
              <div>
                <Label className="mb-2 block">Live-Vorschau</Label>
                <div 
                  className="rounded-xl overflow-hidden border"
                  style={{ height: '500px' }}
                >
                  <iframe
                    src={embedUrl}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    title="Portal Vorschau"
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Save Button */}
          <div className="flex justify-end mt-6 pt-4 border-t">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-green-500 hover:bg-green-600"
            >
              {saving ? 'Wird gespeichert...' : 'Einstellungen speichern'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalSettingsComponent;
